import { appI18n } from '@flowus/common/i18n';
import { Switch } from '@flowus/common/components/switch';
import { cx } from '@flowus/common/cx';
import { useOpenModal } from '@flowus/common/next-modal';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Button } from 'src/common/components/button';
import { Divider } from 'src/common/components/divider';
import { Icon } from 'src/common/components/icon';
import { Input } from 'src/common/components/input';
import { ListView } from 'src/common/components/list-view';
import { ListItemType } from 'src/common/components/list-view/types';
import { publicLink } from 'src/common/const';
import { usePublicShare } from 'src/hooks/page/use-public-share';
import { transaction } from 'src/hooks/use-transaction';
import { bizTracker } from 'src/utils/biz-tracker';
import { usePickBlock } from 'src/utils/pick-block';
import { HelperIcon } from '../helper-icon';
import { RebatePlan } from '../rebate-plan';
import { SharePanelHeader } from './helper';
import { useOpenPreviewPage } from './open-preview-page';
import type { ShareProps } from './types';
import { message } from '@flowus/common/components/message';
import { BuyWelfareCodeModal } from './buy-welfare-code-modal';
import { Modals } from 'src/modals';
import { WelfareCodeList } from 'src/views/main/setting-modal/space-info-setting/welfare-code-list';
import { IncomeRecord } from 'src/views/main/setting-modal/space-info-setting/income-record';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';

const MAX_AMOUNT = 10000;
const MIN_AMOUNT = __BUILD_IN__ ? 0.5 : 0.01;
/**
 * 免费试读：分为不可试读、30个块、50个块、100个块，默认不可试读。当选择可试读后，下方增加【预览】按钮
 */
const previewBlockKeys = [0, 10, 30, 50];

// 付费周期：约等于 永久有效、12个月、6个月、3个月、1个月，默认【永久有效】
// 由于每个月份天数不同，用天表示
const validDayKeys = [0, 365, 180, 90, 30];

/** 付费订阅设置页面 */
export const SharePay: FC<Pick<ShareProps, 'uuid' | 'backToMainPanel'>> = (props) => {
  const { uuid, backToMainPanel } = props;
  const { t } = useAppTranslation();
  const { set, permissionsState, roleStatus, openParentRoleLimit } = usePublicShare(uuid);
  const {
    allowSubscribe,
    accessFee = 0,
    openPreview = false,
    previewBlockCount = 0,
    accessFeeValidDays = 0,
    originPrice: _originPrice = 0,
  } = permissionsState;
  const [sharePageDistributionRatio, setSharePageDistributionRatio] = useState(
    permissionsState.sharePageDistributionRatio
  );
  const block = usePickBlock(uuid, []);
  const [money, setMoney] = useState<number>(accessFee);
  const [noPay, setCheck] = useState(!money);
  const openModal = useOpenModal();
  const [previewCount, setPreviewCount] = useState(
    openPreview === true && previewBlockCount === 0 ? 10 : previewBlockCount
  );
  const [validDays, setValidDays] = useState(accessFeeValidDays);
  const [originPrice, setOriginPrice] = useState(_originPrice === 0 ? undefined : _originPrice);

  const openPreviewPage = useOpenPreviewPage();

  const enableSave = useMemo(() => {
    if (noPay) {
      return true;
    }
    if (!money) {
      return false;
    }
    if (originPrice) {
      if (Number(money) >= Number(originPrice)) {
        return false;
      }
    }
    return true;
  }, [money, noPay, originPrice]);

  const getPreviewBlockLabel = (count: number): string => {
    switch (count) {
      case 0:
        return t('components.share.share_pay.no_preview');
      case 10:
        return t('components.share.share_pay.preview_10_blocks');
      case 30:
        return t('components.share.share_pay.preview_30_blocks');
      case 50:
        return t('components.share.share_pay.preview_50_blocks');
      default:
        return '';
    }
  };

  const getValidDayLabel = (days: number): string => {
    if (days === 0) {
      return t('common.permanent_valid');
    }
    return `${days}${t('common.day')}`;
  };

  // 执行保存的方法，包含完整校验，返回是否成功
  const doSave = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if (roleStatus.isRestricted) {
        resolve(false);
        return;
      }

      const _money = Number(money);
      // flowus最小限制10元
      const limit = __BUILD_IN__ ? 1 : 10;
      if (_money < limit) {
        message.warning(t('components.share.share_pay.fee_less_than_limit', { limit }));
        resolve(false);
        return;
      }

      const _executeSave = () => {
        // 埋点
        if (noPay) {
          bizTracker.event('share_pay_cancel');
        } else {
          if (accessFee !== _money) {
            bizTracker.event('share_pay_open');
          }
        }
        transaction(() => {
          void set().setPartialPermission({
            allowSubscribe: _money ? true : allowSubscribe,
            allowSeo: false,
            accessFee: noPay ? 0 : (_money <= 0 ? 0 : _money) || 0,
            openPreview: noPay ? false : previewCount > 0,
            sharePageDistributionRatio: noPay ? 0 : sharePageDistributionRatio,
            previewBlockCount: previewCount,
            accessFeeValidDays: validDays,
            originPrice: originPrice ? Number(originPrice) : undefined,
          });
        });
        resolve(true);
      };

      // 之前设置过并且修改了金额才弹出提示
      if (accessFee && money && money !== accessFee && !__BUILD_IN__) {
        openModal.warning({
          title: t('components.share.pay.fee_change_warning'),
          content: __BUILD_IN__
            ? t('components.share.pay.fee_change_warning_content_buildin')
            : t('components.share.pay.fee_change_warning_content'),
          confirm() {
            _executeSave();
          },
          cancel() {
            resolve(false);
          },
        });
      } else {
        _executeSave();
      }
    });
  };

  const save = async () => {
    const success = await doSave();
    if (success) {
      backToMainPanel();
    }
  };

  // 购买兑换码前先保存数据
  const handleBuyWelfareCode = async () => {
    const success = await doSave();
    if (!success) return;

    const _money = Number(money);
    openModal.modal({
      content: ({ onCloseModal }) => (
        <BuyWelfareCodeModal
          uuid={uuid}
          spaceId={block?.spaceId ?? ''}
          accessFee={_money}
          onClose={onCloseModal}
        />
      ),
    });
  };

  const openPay = (status: boolean) => {
    if (status && accessFee) {
      openModal.warning({
        title: t('components.share.share_pay.close_payment_warning'),
        confirm: () => {
          setCheck(status);
        },
      });
      return;
    }
    setMoney(0);
    setCheck(status);
  };

  const previewData = previewBlockKeys.map((count) => {
    const title = getPreviewBlockLabel(count);
    return {
      type: ListItemType.TEXT_DESCRIPTION,
      data: {
        title,
        selected: count === previewCount,
        onClick: () => {
          setPreviewCount(count);
          // // 由于要支持预览，因此这里设置后需要先保存到后台
          // transaction(() => {
          //   void set().setPartialPermission({
          //     previewBlockCount: count,
          //   });
          // });
        },
      },
    };
  });

  const validDayData = validDayKeys.map((count) => {
    const title = getValidDayLabel(count);
    return {
      type: ListItemType.TEXT_DESCRIPTION,
      data: {
        title,
        selected: count === validDays,
        onClick: () => {
          setValidDays(count);
        },
      },
    };
  });

  const formatInput = (value: number) => {
    const xiaoShu = value.toString().split('.')[1];
    if (xiaoShu && xiaoShu.length > 2) {
      value = Number(Number(value).toFixed(2));
    }

    if (value > MAX_AMOUNT) {
      value = MAX_AMOUNT;
    }
    return value;
  };

  return (
    <div className="select-none">
      <SharePanelHeader
        title={
          <>
            <span>{t('common.paid_subscribe')}</span>
            <HelperIcon
              placement="bottom"
              popup={
                <div className="space-y-2">
                  <div>{t('components.share.share_pay.pay_subscribe_description')}</div>
                  <a
                    href={publicLink.subscribeCenter}
                    target="_blank"
                    className="text-active_color ml-auto"
                  >
                    {t('components.share.share_pay.view_knowledge_base')}
                  </a>
                </div>
              }
            />
          </>
        }
      />
      {roleStatus.isRestricted && (
        <div
          className="text-t4 bg-grey6 px-4 py-2 animate-click flex justify-between"
          onClick={openParentRoleLimit}
        >
          <span>{t('common.inherit_parent_setting')}</span>
          <span className="text-active_color mx-1">
            {t('components.share.share_pay.remove_inheritance')}
          </span>
        </div>
      )}
      <div className={cx('text-t2', roleStatus.isRestricted && 'pointer-events-none opacity-30')}>
        <div
          className="flex px-4 items-center justify-between animate-click py-2.5 space-x-1"
          onClick={() => openPay(!noPay)}
        >
          <span>{t('common.paid_subscribe')}</span>
          <Switch bgClassName="pointer-events-none" open={!noPay} />
        </div>

        {noPay && <Divider className="my-1" />}
        {!noPay && (
          <>
            <div className="px-4">
              {[
                {
                  autoFocus: true,
                  title: t('common.pay_fee_amount'),
                  readonly: roleStatus.isRestricted,
                  value: (money || '').toString(),
                  placeholder: t('components.share.share_pay.fee_amount_placeholder'),
                  onChange: (value: number) => {
                    value = formatInput(value);
                    setMoney(value);
                    if (value && noPay) {
                      setCheck(false);
                    }
                  },
                },
                {
                  title: (
                    <div>
                      {t('components.share.share_pay.strikethrough_fee')}
                      <HelperIcon
                        placement="bottom"
                        popup={t('components.share.share_pay.strikethrough_fee_description')}
                      />
                    </div>
                  ),
                  readonly: roleStatus.isRestricted,
                  error: money && originPrice ? Number(money) >= Number(originPrice) : false,
                  value: (originPrice || '').toString(),
                  placeholder: t('components.share.share_pay.strikethrough_fee_placeholder'),
                  onChange: (value: number) => {
                    value = formatInput(value);
                    setOriginPrice(value);
                  },
                },
              ].map((item) => (
                <div className="flex items-center py-2.5 justify-between" key={item.placeholder}>
                  <div className="text-t3 shrink-0">{item.title}</div>
                  <Input
                    autoFocus={item.autoFocus}
                    type="number"
                    inputType="number"
                    disableFloat={false}
                    readonly={item.readonly}
                    value={item.value}
                    onChange={item.onChange}
                    maxLength={8}
                    className={'h-8 border-none w-4/5 bg-transparent flex-1'}
                    inputClassName={cx('text-right px-1', item.error && 'text-red')}
                    // inputClassName={cx('text-right px-1')}
                    showBorder={false}
                    placeholder={item.placeholder}
                    addonAfter={t('common.yuan')}
                  />
                </div>
              ))}
              <div
                hidden={__BUILD_IN__}
                className="animate-click flex items-center justify-between py-2.5"
                onClick={(e) => {
                  openModal.dropdown({
                    popcorn: e.currentTarget,
                    content: ({ onCloseModal }) => {
                      return (
                        <ListView
                          className="py-1 next-modal w-[300px]"
                          items={validDayData}
                          onItemClick={() => {
                            onCloseModal();
                          }}
                        />
                      );
                    },
                  });
                }}
              >
                <div className="text-t3">{t('components.share.share_pay.pay_period')}</div>
                <div className="space-x-1 text-grey3">
                  <span>{computePeriod(validDays)}</span>
                  <Icon size="xxsmall" name="IcArrowDown01" />
                </div>
              </div>
            </div>
            <Divider className="my-1" />
            <div className="px-4">
              <div
                className="animate-click flex items-center justify-between py-2.5"
                onClick={(e) => {
                  openModal.dropdown({
                    popcorn: e.currentTarget,
                    content: ({ onCloseModal }) => {
                      return (
                        <ListView
                          className="py-1 next-modal w-[300px]"
                          items={previewData}
                          onItemClick={() => {
                            onCloseModal();
                          }}
                        />
                      );
                    },
                  });
                }}
              >
                <div className="text-t3">{t('components.share.share_pay.free_preview')}</div>
                <div className="space-x-1 text-grey3">
                  <span>{getPreviewBlockLabel(previewCount)}</span>
                  <Icon size="xxsmall" name="IcArrowDown01" />
                </div>
              </div>
              {!__BUILD_IN__ && (
                <RebatePlan
                  className="flex items-center justify-between py-2.5"
                  setSharePageDistributionRatio={setSharePageDistributionRatio}
                  sharePageDistributionRatio={sharePageDistributionRatio}
                >
                  <div className="text-t3">{t('components.share.share_pay.commission_ratio')}</div>
                </RebatePlan>
              )}
            </div>

            {/* 兑换码批量购买模块 */}
            {!__BUILD_IN__ && money > 0 && (
              <>
                <Divider className="my-1" />
                <div className="py-3">
                  <div className="px-4">
                    <div className="text-t2-medium mb-2">
                      {t('components.share.share_pay.redemption_code_bulk_purchase')}
                    </div>
                    <div className="text-t4 text-grey3 mb-2">
                      {t('components.share.share_pay.redemption_code_unit_price', {
                        price: (money * 0.1).toFixed(2),
                      })}
                    </div>
                    <div className="text-t4 text-grey3 mb-3">
                      {t('components.share.share_pay.redemption_code_description')}
                      <span className="text-red font-medium">
                        {t('components.share.share_pay.redemption_code_warning')}
                      </span>
                    </div>
                    <Button colorType="active" onClick={handleBuyWelfareCode}>
                      {t('common.buy_redemption_code')}
                    </Button>
                  </div>
                  <Divider className="my-1" />
                  {/* 我的兑换码入口 */}
                  <div
                    className="px-4 mt-3 flex cursor-pointer items-center justify-between text-t2"
                    onClick={() => {
                      openModal.modal({
                        modalId: Modals.INCOME_RECORD,
                        content: () => <IncomeRecord defaultTabIndex={2} />,
                      });
                    }}
                  >
                    <span>{t('common.my_redemption_codes')}</span>
                    <Icon size="xxsmall" name="IcArrowDown01" className="-rotate-90" />
                  </div>
                </div>
              </>
            )}

            <Divider className="my-1" />
            {!__BUILD_IN__ && (
              <div className="text-grey3 py-1 text-t3 px-4">
                <span>{t('components.share.share_pay.pay_share_agreement_prefix')}</span>
                &nbsp;
                <a href={publicLink.creator} className="text-active_color" target="_blank">
                  {t('components.share.share_pay.creator_agreement')}
                </a>
              </div>
            )}
          </>
        )}

        <div className="flex items-center justify-between py-2.5 px-4">
          <div>
            {previewCount > 0 && !noPay && (
              <Button
                colorType="primary"
                onClick={() =>
                  openPreviewPage({
                    accessFee,
                    spaceId: block?.spaceId ?? '',
                    uuid,
                    previewCount,
                  })
                }
              >
                {t('common.preview')}
              </Button>
            )}
          </div>
          <div className="flex space-x-2.5">
            <Button
              colorType={'secondary'}
              onClick={() => {
                backToMainPanel();
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button colorType={'active'} onClick={save} disable={!enableSave}>
              {t('common.save')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const computePeriod = (value: number | undefined) => {
  if (value === undefined || value === 0) {
    return appI18n.t('common.permanent_valid');
  }
  return `${value}${appI18n.t('common.day')}`;
};
